import React, {useEffect, useState} from 'react';
import CustomDropdown from '../../../components/ui/CustomDropdown';
import AuthPrimaryButton from '../../../components/ui/AuthPrimaryButton';
import {
    modalBody,
    modalCloseBtn,
    modalField,
    modalLabel,
    modalStack,
    modalSubtitle,
    modalTitle,
} from '../../../components/ui/modalDesign';
import {toast} from 'react-toastify';
import UserService from '../../../services/user.service';
import { X } from 'lucide-react';
import {filterDaysOfTheWeek, restructureTimeData} from "../../../utils/Tools";
import {useSelector} from "react-redux";
import {RootState} from "../../../redux/rootReducer";
import {MIN_AGE_FOR_REG, filterOpenCohorts, resolvePackageForChild, isAdvancedAge, ADVANCED_PACKAGE_LEVEL} from "../../../utils/constants";

export interface ChildRegProps {
}

const initialValues = {}

interface Props {
    handleNext: () => void;
    setChildInfo: (_arg1: number, _arg2: number, _cohortId: number) => void;
    closeModalOnOutsideClick: (data: boolean) => void;
    closeRegTab: () => void;
}


const inputFieldStyle = modalField;
const labelStyle = modalLabel;

//global time storage
let staticTime: any = []
let cohortKeep: any = []

export default function RegRenewalModal({handleNext, setChildInfo, closeModalOnOutsideClick, closeRegTab}: Props) {
    // const userInfo: any = useSelector((state:RootState) => state.auth.userInfo);
    const userInfoTarget: any = useSelector((state: RootState) => state.user.child);
    const userService = new UserService();

    const [formData, setFormData] = useState(initialValues);
    const [dayTime, setDayTime] = useState<any>([]);
    const [dayArr, setDayArr] = useState<{ name: string; value: number; }[] | []>([]);
    const [timeArr, setTimeArr] = useState<{ name: string; value: number; }[] | []>([]);
    const [cohortId, setCohortId] = useState(0);
    const [cohortArr, setCohortArr] = useState<{ name: string; value: number; }[] | []>([]);

    const [selectedTime, setSelectedTime] = useState<{ name: string, value: number } | null>(null);
    const [selectedDay, setSelectedDay] = useState<{ name: string, value: number } | null>(null);
    const [loading, setLoading] = useState(false);
    //new states
    const [packages, setPackages] = useState<any>([])
    const [selectedPackage, setSelectedPackage] = useState<any>()
    const [selectedSubPackage, setSelectedSubPackageId] = useState<any>(null)

    // load all available days and their corresponding time for BE
    const getDayTime = async () => {
        try {
            const response = await userService.getDayTime();
            if (!response.status) {
                toast.error(response.message)
                return;
            }
            setDayTime(response.data);
            staticTime = response.data
            if (response?.data?.length > 0) {
                // extracted dayText and day values and saved them as 'name' and 'value' respectively for ease of use in the custom dropdown component
                setDayArr(filterDaysOfTheWeek(response?.data))
            }
            // dispatch(setDayTimeInfo(response.data))

            const responsePkg = await userService.getAllPackages()
            if (!responsePkg.status) {
                toast.error(response.message)
                return;
            }
            setPackages(responsePkg.data)

            const responseCohort = await userService.getCohort()
            if (!responseCohort.status) {
                toast.error(responseCohort.message)
                return;
            }
            cohortKeep = responseCohort.data
            // Filter cohorts immediately based on current age if available
            if (userInfoTarget?.age >= MIN_AGE_FOR_REG) {
                setCohortArr(filterOpenCohorts(cohortKeep))
            } else {
                // If age not set yet, show all cohorts (will be filtered when age is set)
                setCohortArr(responseCohort.data);
            }
        } catch (err: any) {
            toast.error(err?.message);
        }
        return false;
    };


    const handleSubmit = async (e: any) => {
        e.preventDefault();
        if (!selectedDay || !selectedTime || !cohortId) {
            toast.error('Date or time or cohort is required!');
            return;
        }

        setChildInfo(selectedDay.value, selectedTime.value, cohortId)
        // console.log('from renew', selectedDay, selectedTime)
        handleNext();
    };


    const keepModalOpen = () => {
        // if all these are unfilled or empty then closing modal by clicking outside is possible
        if (selectedTime === null && selectedDay === null) {
            closeModalOnOutsideClick(true)
        } else {
            closeModalOnOutsideClick(false)
        }
    }


    useEffect(() => {
        getDayTime();
        keepModalOpen();
    }, []);

    useEffect(() => {
        keepModalOpen();
    }, [formData])

    useEffect(() => {
        if (selectedDay) {
            const timeX = dayTime?.filter((item: any) => item.dayText === selectedDay.name);
            const tdx = timeX[0].times;
            // extracted timeText and time values and saved them as 'name' and 'value' respectively for ease of use in the custom dropdown component
            let arr = []
            for (let i = 0; i < tdx.length; i++) {
                let name = tdx[i].timeText;
                let value = tdx[i].time;
                let _dy = {name, value};
                arr.push(_dy);
            }
            setTimeArr(arr)
        }
    }, [selectedDay]);

    useEffect(() => {
        if (userInfoTarget?.age >= MIN_AGE_FOR_REG) {
            const _age = userInfoTarget.age

            //check programs
            const hasPrograms = Array.isArray(userInfoTarget?.programs)
            const hasProgramAndPackages = (hasPrograms && userInfoTarget?.programs[0].hasOwnProperty("package"))
            //get old package and check last levels — age >16 always uses Advanced Level 14
            const _oPkgLevel = isAdvancedAge(_age)
                ? ADVANCED_PACKAGE_LEVEL
                : hasProgramAndPackages
                    ? Number(userInfoTarget?.level) || 1
                    : 1
            const pkg = resolvePackageForChild(packages, _age, _oPkgLevel)
            if (pkg) {
                setSelectedPackage(pkg)
                localStorage.setItem("package", JSON.stringify(pkg))
                //offload remote times
                if (pkg?.timeGroup) {
                    //this has a unique time
                    const remTimeData = restructureTimeData(pkg?.timeGroup?.times)
                    setDayArr(filterDaysOfTheWeek(remTimeData))
                    setDayTime(remTimeData)
                } else {
                    //populate normal time
                    setDayTime(staticTime)
                    setDayArr(filterDaysOfTheWeek(staticTime))
                }
            }
        }
    }, [packages, userInfoTarget?.age, userInfoTarget?.level]);

    // Filter cohorts based on age - separate effect to ensure it always runs
    useEffect(() => {
        if (userInfoTarget?.age >= MIN_AGE_FOR_REG && cohortKeep && cohortKeep.length > 0) {
            setCohortArr(filterOpenCohorts(cohortKeep))
        }
    }, [userInfoTarget?.age]);

    return (
        <form className={`relative ${modalBody}`} onSubmit={handleSubmit}>
            <button type="button" aria-label="Close" className={modalCloseBtn} onClick={closeRegTab}>
                <X size={18} />
            </button>

            <div className="mb-5 pr-8 text-center sm:mb-6">
                <h1 className={modalTitle}>Add a Program</h1>
                <p className={modalSubtitle}>Choose lesson day, time, and cohort for this child.</p>
            </div>

            <div className={modalStack}>
                <div>
                    <label className={labelStyle}>Program</label>
                    <p className={`${inputFieldStyle} text-[#8b9aab]`}>
                        {selectedPackage?.title || 'Loading program…'}
                    </p>
                </div>

                <div>
                    <label className={labelStyle}>Lesson day</label>
                    <CustomDropdown
                        className={inputFieldStyle}
                        handleChange={(data: { name: string, value: number }) => setSelectedDay(data)}
                        data={dayArr}
                    />
                </div>

                <div>
                    <label className={labelStyle}>Lesson time</label>
                    <CustomDropdown
                        className={inputFieldStyle}
                        handleChange={(data: { name: string, value: number }) => setSelectedTime(data)}
                        data={timeArr}
                    />
                </div>

                <div>
                    <label className={labelStyle}>Choose cohort</label>
                    <select
                        className={inputFieldStyle}
                        onChange={(e: any) => setCohortId(Number(e.target.value))}
                    >
                        <option value={0}>Choose cohort</option>
                        {cohortArr && cohortArr.map((d: any, i: number) => (
                            <option key={i} value={d.id}>{d.title}</option>
                        ))}
                    </select>
                </div>

                <AuthPrimaryButton loading={loading}>Next</AuthPrimaryButton>
            </div>
        </form>
    )
}
